import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Num, SectionTitle } from '../components/Type';
import { plates, markHeight } from '../content';
import type { Plate as PlateT } from '../content';
import { CalendarIcon, LockIcon } from '../components/Icons';
import PinGate, { isUnlocked } from '../components/PinGate';
import Figure from '../components/Figure';

/* The project page. Everything the index deliberately leaves out ends
   up here: the full standfirst, the engagement facts, and the prose. */

/* Portrait figures that sit next to each other are paired into one
   row. Anything else stands alone, at the full measure. */
type Fig = NonNullable<PlateT['sections'][number]['figures']>[number];

const isTall = (f: Fig) => {
  if (!f.ratio) return false;
  const [w, h] = f.ratio.split('/').map((n) => parseFloat(n));
  return w > 0 && h > 0 && w / h < 0.8;
};

const groupFigures = (figs: Fig[]): Fig[][] =>
  figs.reduce<Fig[][]>((rows, fig) => {
    const last = rows[rows.length - 1];
    if (last && last.length === 1 && isTall(fig) && isTall(last[0])) last.push(fig);
    else rows.push([fig]);
    return rows;
  }, []);

const PlatePage: React.FC = () => {
  const { id } = useParams();
  const plate = plates.find((p) => p.id === id);
  const index = plates.findIndex((p) => p.id === id);
  const next = plates[(index + 1) % plates.length];

  /* The next-project link was the one way past the gate: it is a
     plain Link, so clicking it walked straight into a locked case.
     It now goes through the same dialog as the index rows. */
  const [gate, setGate] = useState<string | null>(null);
  const [open, setOpen] = useState(isUnlocked());
  const nextTo = `/work/${next.id}`;
  /* Same rule as the index: the first project is open, the rest wait. */
  const nextLocked = !open && next.id !== plates[0].id;

  if (!plate) {
    return (
      <section className="canvas">
        <div className="section-head">
          <SectionTitle as="h1">Not found</SectionTitle>
          <div className="range">No project under that name</div>
        </div>
        <Link to="/work" className="entry-read">
          Back to the work <span className="arrow">→</span>
        </Link>
      </section>
    );
  }

  const facts = [
    { k: 'Client', v: plate.clientFull || plate.client },
    { k: 'Year', v: plate.year },
    { k: 'Role', v: plate.role },
    { k: 'Duration', v: plate.duration },
    { k: 'Sector', v: plate.sector },
  ].filter((f) => f.v);

  return (
    <article className="canvas page">
      <header className="case-head">
        <div className="case-head-left">
        {/* The mark belongs here, at a size where it is legible, rather
            than shrunk into the index where the client's name already
            scans faster than a logo does. */}
        {plate.logo && (
          <img
            className="case-mark client-mark"
            src={plate.logo}
            alt={plate.clientFull || plate.client}
            style={{ height: markHeight(plate.logo) }}
          />
        )}

        <div className="case-eyebrow">
          <span>{plate.clientFull || plate.client}</span>
          {plate.sector && <span className="sep">·</span>}
          {plate.sector && <span>{plate.sector}</span>}
        </div>

        <h1 className="case-title">
          {plate.titleTop}
          {plate.titleItalic && (
            <>
              <br />
              <span className="it">{plate.titleItalic}</span>
            </>
          )}
        </h1>
        </div>

        <div className="case-head-right">
        <p className="case-dek">{plate.dek}</p>

        <dl className="case-facts">
          {facts.map((f) => (
            <div className="case-fact" key={f.k}>
              <dt>{(f.k === 'Year' || f.k === 'Duration') && <CalendarIcon />}{f.k}</dt>
              <dd>{typeof f.v === 'string' ? <Num>{f.v}</Num> : f.v}</dd>
            </div>
          ))}
        </dl>
        </div>
      </header>

      {/* The film sits directly under the cover, at the full measure
          and larger than any still on the page. It never downloads
          until asked: twelve megabytes is not something to spend on
          a reader who may not watch it. */}
      {plate.film && (
        <figure className="case-film">
          <video
            src={plate.film.src}
            poster={plate.film.poster}
            controls
            playsInline
            preload="none"
          />
          <figcaption>
            <span>{plate.film.caption}</span>
            <span className="case-film-len">{plate.film.length}</span>
          </figcaption>
        </figure>
      )}

      {/* The case study. Each movement keeps the label it had in the
          static edition — The Brief, The Work, and so on — in the rail. */}
      {plate.sections.map((sec, i) => (
        <section className="case-section" key={sec.label || i}>
          <div className="case-section-label">{sec.label}</div>
          <div className="case-section-body">
            {sec.heading && <h2 className="case-section-heading">{sec.heading}</h2>}
            <div className="wall-text">
              {sec.paras.map((para, n) => (
                <p key={n}>
                  {i === 0 && n === 0 ? (
                    <>
                      <span className="drop">{para.charAt(0)}</span>
                      {para.slice(1)}
                    </>
                  ) : (
                    para
                  )}
                </p>
              ))}

              {sec.bullets && sec.bullets.length > 0 && (
                <ul className="case-list">
                  {sec.bullets.map((b, n) => (
                    <li key={n}>{b}</li>
                  ))}
                </ul>
              )}
            </div>

            {sec.figures && groupFigures(sec.figures).map((group) =>
              group.length > 1 ? (
                /* Two phone screens stacked at the full measure run to
                   three thousand pixels and read as two separate
                   thoughts. Side by side they read as one. */
                <div className="figure-pair" key={group[0].src}>
                  {group.map((fig) => <Figure key={fig.src} {...fig} />)}
                </div>
              ) : (
                <Figure key={group[0].src} {...group[0]} />
              ),
            )}
          </div>
        </section>
      ))}

      {plate.outcomes.length > 0 && (
        <section className="case-section case-outcomes">
          <div className="case-section-label">Outcomes</div>
          <div className="case-section-body">
            <dl className="outcomes-grid">
              {plate.outcomes.map((o) => (
                <div className="outcome" key={o.label}>
                  <dt className="outcome-stat">{o.stat}</dt>
                  <dd className="outcome-label">{o.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      <div className="entry-nav">
        <Link to="/work" className="entry-read">
          <span className="arrow">←</span> All work
        </Link>
        {nextLocked ? (
          <button
            type="button"
            className="entry-read as-button"
            onClick={() => setGate(nextTo)}
          >
            <LockIcon />
            {next.clientFull || next.client} <span className="arrow">→</span>
          </button>
        ) : (
          <Link to={nextTo} className="entry-read">
            {next.clientFull || next.client} <span className="arrow">→</span>
          </Link>
        )}
      </div>

      <PinGate to={gate} onClose={() => { setGate(null); setOpen(isUnlocked()); }} />
    </article>
  );
};

export default PlatePage;
